<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-stately/selection';
import collectionsDocs from 'docs:@react-types/shared/src/collections.d.ts';
import ariaDocs from 'docs:@react-aria/selection';
import listDocs from 'docs:@react-stately/list';
import treeDocs from 'docs:@react-stately/tree';
import {HeaderInfo, ClassAPI, TypeContext, InterfaceType, TypeLink} from '@react-spectrum/docs';
import packageData from '@react-stately/selection/package.json';

```jsx import
import '@react-spectrum/docs/src/client';
```

---
category: Selection
---

# SelectionManager

<p>{docs.exports.SelectionManager.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['SelectionManager']} />

## Introduction

A <TypeLink links={docs.links} type={docs.exports.SelectionManager} /> provides a generic interface for reading and
updating selection and focus state based on a <TypeLink links={collectionsDocs.links} type={collectionsDocs.exports.Collection} />.
As discussed in the [selection introduction](selection.html), a selection is represented
by a [Set](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set) of item keys. Focus
is represented by a single item key.

Focus state is updated when navigating a collection with the keyboard. Selection state is updated when a user
clicks or taps an item, or uses the keyboard to select an item. These interactions are handled by
the <TypeLink links={ariaDocs.links} type={ariaDocs.exports.useSelectableCollection} /> hook in [react-aria](/react-aria/index.html).

A `SelectionManager` wraps the state returned by <TypeLink links={docs.links} type={docs.exports.useMultipleSelectionState} />.
Oftentimes, you won't need to construct these directly because hooks like <TypeLink links={listDocs.links} type={listDocs.exports.useListState} />
and <TypeLink links={treeDocs.links} type={treeDocs.exports.useTreeState} /> already handle this and return
a `SelectionManager` for you.

## Interface

<ClassAPI links={docs.links} class={docs.exports.SelectionManager} />
